import { Routes, Route, Navigate } from 'react-router-dom'
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MobileLayout, DesktopLayout } from '@/components/templates/layouts'
import MainScreen from '@/screens/MainScreen'
import TradingScreen from '@/screens/TradingScreen'
import DataRoomScreen from '@/screens/DataRoomScreen'
import PartnerPortalScreen from '@/screens/PartnerPortalScreen'
import PartnerReferralsScreen from '@/screens/PartnerReferralsScreen'
import PartnershipsScreen from '@/screens/PartnershipsScreen'
import { LoginScreen } from '@/screens/login'
import { SignupScreen } from '@/screens/signup'
import { AttestationScreen } from '@/screens/attestation'
import { ProfilePreferencesScreen } from '@/screens/profile-preferences'
// ProfileSettingsScreen removed - functionality moved to ProfileScreen
import ProfileScreen from '@/screens/ProfileScreen'
import ProjectDetailScreen from '@/screens/data-room/ProjectDetailScreen'
import { useDeviceType } from '@/hooks/useDeviceType'

// Portfolio Tracker imports
import PortfolioTrackerScreen from '@/screens/portfolio/PortfolioTrackerScreen'
import PortfolioCreateScreen from '@/screens/portfolio/PortfolioCreateScreen'
import PortfolioDetailScreen from '@/screens/portfolio/PortfolioDetailScreen'
import PortfolioAssetsScreen from '@/screens/portfolio/PortfolioAssetsScreen'
import AssetDetailScreen from '@/screens/portfolio/AssetDetailScreen'

import { authService } from '@/services/authService'
import { attestationService } from '@/services/attestationService'
import { useTheme } from '@/hooks/useTheme'

// Create a client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache kept in memory (formerly cacheTime)
      retry: 1, // Only retry once on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    },
  },
})

function App() {
  const { isMobile } = useDeviceType()
  // Initialize theme based on system preference
  useTheme()

  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [hasAttestation, setHasAttestation] = useState<boolean | null>(null)
  const attestationCheckedRef = React.useRef(false)

  useEffect(() => {
    let isMounted = true
    
    // Check authentication status
    const checkAuth = async () => {
      try {
        const isAuth = authService.isAuthenticated()
        if (isAuth) {
          // Validate session
          await authService.validateSession()
          if (isMounted) {
            setIsAuthenticated(true)
          }
          
          // Check attestation status for authenticated users (only once)
          if (!attestationCheckedRef.current && isMounted) {
            attestationCheckedRef.current = true
            try {
              const attestationStatus = await attestationService.getAttestationStatus()
              if (isMounted) {
                setHasAttestation(attestationStatus.has_attestation)
              }
            } catch (error) {
              console.error('Attestation status check failed:', error)
              // Default to requiring attestation if check fails
              if (isMounted) {
                setHasAttestation(false)
              }
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        authService.logout()
        if (isMounted) {
          setIsAuthenticated(false)
          setHasAttestation(null)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    checkAuth()

    // Listen for auth changes
    const checkAuthPeriodically = setInterval(() => {
      if (isMounted) {
        const isAuth = authService.isAuthenticated()
        setIsAuthenticated(isAuth)
      }
    }, 1000)

    return () => {
      isMounted = false
      clearInterval(checkAuthPeriodically)
    }
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gradient">SecondLane</h2>
          <p className="text-foreground opacity-60">Loading trading portal...</p>
        </motion.div>
      </div>
    )
  }

  // Protected Route Component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />
    }
    
    // Check if user needs attestation
    if (hasAttestation === false) {
      return <Navigate to="/attestation" replace />
    }
    
    return <>{children}</>
  }

  // Auth Route Component (redirect to home if already authenticated)
  const AuthRoute = ({ children }: { children: React.ReactNode }) => {
    return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
  }

  return (
    <QueryClientProvider client={queryClient}>
    <div className="min-h-screen bg-background text-foreground">
      <AnimatePresence mode="wait">
        <Routes>
          {/* Authentication Routes */}
          <Route 
            path="/login" 
            element={
              <AuthRoute>
                <LoginScreen />
              </AuthRoute>
            } 
          />
          <Route 
            path="/signup" 
            element={
              <AuthRoute>
                <SignupScreen />
              </AuthRoute>
            } 
          />

          {/* Attestation Route */}
          <Route 
            path="/attestation" 
            element={
              isAuthenticated ? <AttestationScreen /> : <Navigate to="/login" replace />
            } 
          />

          {/* Profile Preferences Route */}
          <Route 
            path="/profile-preferences" 
            element={
              isAuthenticated ? <ProfilePreferencesScreen /> : <Navigate to="/login" replace />
            } 
          />

          {/* Protected Routes */}
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppContent isMobile={isMobile} />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </AnimatePresence>
    </div>
    </QueryClientProvider>
  )
}

// App Content Component (for authenticated users)
const AppContent = ({ isMobile }: { isMobile: boolean }) => {
  const Layout = isMobile ? MobileLayout : DesktopLayout

  return (
    <Layout>
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<MainScreen />} />
          <Route path="/trading/*" element={<TradingScreen />} />
          {/* <Route path="/yield/*" element={<YieldScreen />} /> */}
          {/* <Route path="/rwa/*" element={<RwaScreen />} /> */}
          <Route path="/data-rooms/:projectId" element={<ProjectDetailScreen />} />
          <Route path="/projects/:projectId" element={<ProjectDetailScreen />} />
          <Route path="/projects/:projectId/unlock" element={<ProjectDetailScreen />} />
          <Route path="/data-room/*" element={<DataRoomScreen />} />
          <Route path="/partnerships/*" element={<PartnershipsScreen />} />
          <Route path="/portfolio" element={<PortfolioTrackerScreen />} />
          <Route path="/portfolio/create" element={<PortfolioCreateScreen />} />
          <Route path="/portfolio/:portfolioId" element={<PortfolioDetailScreen />} />
          <Route path="/portfolio/:portfolioId/assets" element={<PortfolioAssetsScreen />} />
          <Route path="/portfolio/:portfolioId/assets/:assetId" element={<AssetDetailScreen />} />
          <Route path="/partnerships/portal" element={<PartnerPortalScreen />} />
          <Route path="/partnerships/referrals" element={<PartnerReferralsScreen />} />
          <Route path="/profile" element={<ProfileScreen />} />
          <Route path="/settings" element={<Navigate to="/profile" replace />} />
        </Routes>
      </AnimatePresence>
    </Layout>
  )
}

export default App 